import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Check,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface AvailabilitySlot {
  day: string;
  startTime: string;
  endTime: string;
  available: boolean;
}

const AvailabilityStatusPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [responseSettings, setResponseSettings] = useState('2-hours');
  const [editing, setEditing] = useState(false);
  const hasCheckedAuth = useRef(false);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id) {
      fetchAvailability();
    }
  }, [user?.id]);

  const initializeDefaultAvailability = () => {
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return daysOfWeek.map((day) => ({
      day,
      startTime: '09:00',
      endTime: '17:00',
      available: true,
    }));
  };

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/availability`);
      if (response.success && Array.isArray(response.data) && response.data.length > 0) {
        setAvailability(response.data);
        if (response.responseSettings) {
          setResponseSettings(response.responseSettings);
        }
      } else {
        setAvailability(initializeDefaultAvailability());
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      // Initialize with default availability on error
      setAvailability(initializeDefaultAvailability());
    } finally {
      setLoading(false);
    }
  };

  const handleAvailabilityChange = (day: string, available: boolean) => {
    setAvailability((prev) =>
      prev.map((slot) =>
        slot.day === day ? { ...slot, available } : slot
      )
    );
  };

  const handleSaveAvailability = async () => {
    try {
      const response = await apiService.put(`/provider/availability`, {
        availability,
        responseSettings,
      });
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Availability updated',
        });
        setEditing(false);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update availability',
        variant: 'destructive',
      });
    }
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <AlertCircle className="w-16 h-16 text-red-500" />
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Availability & Status - VireWire Provider</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Availability / Status</h1>
            <p className="text-gray-600 text-sm mt-1">
              Control your schedule and job availability settings
            </p>
          </div>
          <Badge className="bg-green-100 text-green-800">AVAILABLE</Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card>
            <CardHeader className="bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <CardTitle className="text-base">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-7 gap-2 mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-xs font-bold text-gray-600">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {calendarDays.map((day, idx) => (
                  <div
                    key={idx}
                    className={`p-2 text-center text-xs rounded ${
                      day === null
                        ? 'text-gray-300'
                        : day === new Date().getDate()
                        ? 'bg-green-100 font-bold text-green-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {day}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Working Hours Schedule */}
          <div className="lg:col-span-2 space-y-4">
            {/* Response Settings */}
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Response Settings</CardTitle>
                  <select
                    value={responseSettings}
                    onChange={(e) => setResponseSettings(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="30-mins">30 minutes</option>
                    <option value="1-hour">1 hour</option>
                    <option value="2-hours">2 hours</option>
                    <option value="4-hours">4 hours</option>
                  </select>
                </div>
              </CardHeader>
            </Card>

            {/* Availability Grid */}
            <Card>
              <CardHeader className="bg-gray-50 border-b flex items-center justify-between">
                <CardTitle className="text-base">Working Hours & Availability</CardTitle>
                {!editing && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(true)}
                    className="gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {daysOfWeek.map((day) => {
                    const slot = availability.find((s) => s.day === day);
                    return (
                      <div
                        key={day}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{day}</p>
                          <p className="text-sm text-gray-600">
                            {slot?.startTime} - {slot?.endTime}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          {editing && (
                            <Switch
                              checked={slot?.available || false}
                              onCheckedChange={(checked) =>
                                handleAvailabilityChange(day, checked)
                              }
                            />
                          )}
                          {!editing && slot?.available && (
                            <Check className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {editing && (
                  <div className="flex gap-3 mt-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setEditing(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveAvailability}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      Save Changes
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Additional Settings */}
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="text-base">Additional Settings</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Max SLA response time</p>
                    <p className="text-xs text-gray-600 mt-1">How quickly you respond to jobs</p>
                  </div>
                  <select className="text-sm border border-gray-300 rounded px-2 py-1">
                    <option>2 hours</option>
                    <option>4 hours</option>
                    <option>6 hours</option>
                  </select>
                </div>

                <div className="border-t pt-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Preferred color period</p>
                    <p className="text-xs text-gray-600 mt-1">Choose your preferred work period</p>
                  </div>
                  <select className="text-sm border border-gray-300 rounded px-2 py-1">
                    <option>Default</option>
                    <option>Morning</option>
                    <option>Afternoon</option>
                    <option>Evening</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Auto-decline requests */}
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="text-base">Auto-decline Requests</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-sm text-gray-600 mb-3">
                  Automatically decline job requests that don't match your preferences
                </p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm text-gray-700">Decline jobs outside working hours</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm text-gray-700">Decline repeat customers (if applicable)</span>
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ProviderLayout>
  );
};

export default AvailabilityStatusPage;
